import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Sparkles, Download, Loader2, ImageIcon, Share2, Gift, PartyPopper, Layers, Wand2, Coins, RotateCcw } from 'lucide-react';
import ShareModal from '@/components/ShareModal';
import ImageActions from '@/components/ImageActions';
import AIModelSelector from '@/components/AIModelSelector';
import LowCreditWarning, { 
  CreditCostDisplay, 
  SmartNudge, 
  ProgressiveCreditWarning,
  ConversionOptimizedActions,
  useEngagementLoop 
} from '@/components/LowCreditWarning';
import { useUpgradeTrigger, UpgradeModal, trackWorkflowAction } from '@/components/UpgradeTrigger';
import { isAdminTester, logAdminBypass } from '@/utils/adminBypass';
import { getToastMessage, requiresUpgrade, getErrorInfo, isRetriableError, classifyError } from '@/utils/errorClassifier';

const ImageGenerator = () => {
  const { api, user, updateCredits, refreshUser } = useAuth();
  const [prompt, setPrompt] = useState('');
  const [style, setStyle] = useState('product');
  const [size, setSize] = useState('1024x1024');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [currentImageUrl, setCurrentImageUrl] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [isFirstFree, setIsFirstFree] = useState(false);
  const [checkingFirstFree, setCheckingFirstFree] = useState(true);
  
  // AI Model selection - consistent with ImageToPrompt and Workflow
  const [selectedModel, setSelectedModel] = useState('gpt');
  
  // Auto-enhance toggle (default ON)
  const [autoEnhance, setAutoEnhance] = useState(true);
  
  // Variations state
  const [generatingVariations, setGeneratingVariations] = useState(false);
  const [variations, setVariations] = useState([]);
  const [variationsRetrying, setVariationsRetrying] = useState(false);
  const [variationsError, setVariationsError] = useState(null);
  const variationsRetryCountRef = useRef(0);
  const MAX_AUTO_RETRIES = 1;
  const RETRY_DELAY = 1500;
  
  // Engagement loop tracking
  const { startLoop, recordStep, resetLoop, LOOP_STEPS } = useEngagementLoop();
  
  // Smart nudge state - shows after generation if user does nothing
  const [showSmartNudge, setShowSmartNudge] = useState(false);
  const [userHasActed, setUserHasActed] = useState(false);
  const smartNudgeTimerRef = useRef(null);
  
  // Track if user has performed improvement actions
  const [hasImproved, setHasImproved] = useState(false);
  const [hasEnhanced, setHasEnhanced] = useState(false);
  const [hasUpscaled, setHasUpscaled] = useState(false);
  
  // Upgrade trigger hook
  const { 
    showUpgradeModal, 
    triggerReason, 
    trackAction, 
    checkLowCredits,
    triggerCreditFailure,
    triggerPlanEntitlement,
    dismissModal,
    setShowUpgradeModal 
  } = useUpgradeTrigger();

  const styles = [
    { value: 'product', label: 'Product Design' },
    { value: 'sticker', label: 'Sticker Style' },
    { value: 'illustration', label: 'Illustration' },
    { value: 'photorealistic', label: 'Photorealistic' },
  ];

  const sizes = [
    { value: '1024x1024', label: 'Square (1024x1024)' },
    { value: '1792x1024', label: 'Landscape (1792x1024)' },
    { value: '1024x1792', label: 'Portrait (1024x1792)' },
  ];

  // Check if user is eligible for first free generation
  useEffect(() => {
    const checkFirstFreeStatus = async () => {
      try {
        const response = await api.get('/ai/first-generation-status');
        setIsFirstFree(response.data.is_eligible_for_free);
      } catch (error) {
        // If check fails, assume not eligible
        setIsFirstFree(false);
      } finally {
        setCheckingFirstFree(false);
      }
    };
    
    checkFirstFreeStatus();
  }, [api]);
  
  // Load image from Gallery if coming from "Open in Editor"
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('fromGallery') === 'true') {
      const editorImage = sessionStorage.getItem('editorImage');
      const editorPrompt = sessionStorage.getItem('editorPrompt');
      const editorStyle = sessionStorage.getItem('editorStyle');
      
      if (editorPrompt) {
        setPrompt(editorPrompt);
      }
      if (editorStyle) {
        setStyle(editorStyle);
      }
      if (editorImage) {
        setCurrentImageUrl(editorImage);
        setResult({ image_base64: editorImage.split(',')[1] || editorImage });
      }
      
      // Clear sessionStorage after loading
      sessionStorage.removeItem('editorImage');
      sessionStorage.removeItem('editorPrompt');
      sessionStorage.removeItem('editorStyle');
      
      // Clean URL
      window.history.replaceState({}, '', '/dashboard/generate');
      toast.info('Image loaded from Gallery. Make changes and regenerate!');
    }
  }, []);

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      toast.error('Please enter a prompt');
      return;
    }

    // Allow generation if first free OR has enough credits OR user is admin (testing mode)
    const isAdmin = isAdminTester(user);
    logAdminBypass('ImageGenerator', 'handleGenerate', user, isAdmin);
    
    if (!isFirstFree && !isAdmin && (user?.credits || 0) < 5) {
      toast.error('Insufficient credits. Please upgrade your plan.');
      return;
    }

    setLoading(true);
    setVariations([]); // Clear any previous variations
    
    // Reset engagement loop state for new generation
    setUserHasActed(false);
    setHasImproved(false);
    setHasEnhanced(false);
    setHasUpscaled(false);
    setShowSmartNudge(false);
    if (smartNudgeTimerRef.current) {
      clearTimeout(smartNudgeTimerRef.current);
    }
    
    try {
      const response = await api.post('/ai/generate-image', {
        prompt: prompt.trim(),
        style,
        size,
        ai_model: selectedModel,
        auto_enhance: autoEnhance
      });
      setResult(response.data);
      // Set current image URL for enhance/upscale
      const imgUrl = response.data.image_base64 
        ? `data:image/png;base64,${response.data.image_base64}` 
        : response.data.image_url;
      setCurrentImageUrl(imgUrl);
      await refreshUser();
      
      // Start engagement loop tracking
      startLoop();
      
      // Show appropriate toast message
      if (response.data.is_first_generation) {
        toast.success(
          <div className="flex items-center gap-2">
            <PartyPopper className="w-5 h-5 text-yellow-500" />
            <span>Your first image is on us! Enjoy creating!</span>
          </div>,
          { duration: 5000 }
        );
        setIsFirstFree(false); // No longer eligible after first generation
      } else {
        toast.success(`Image generated! ${response.data.credits_used} credits used`);
      }
      
      // Track workflow action for upgrade trigger
      trackAction('generate');
      trackWorkflowAction('generate');
      
      // Check if we should show low credits nudge
      checkLowCredits();
      
      // Trigger smart nudge after 3 seconds if user doesn't act
      // (3s = natural pause point, not too aggressive, not too slow)
      smartNudgeTimerRef.current = setTimeout(() => {
        if (!userHasActed) {
          setShowSmartNudge(true);
        }
      }, 3000);
      
    } catch (error) {
      // Standardized error classification
      const errorInfo = getErrorInfo(error, user);
      
      // Show user-friendly error message
      toast.error(getToastMessage(error, 'Image generation failed. No credits were used.'));
      
      // Trigger upgrade modal for credit/plan errors (ONLY for normal users)
      // Never trigger for admin, never trigger for provider errors
      if (!isAdminTester(user) && requiresUpgrade(error)) {
        const errorType = classifyError(error);
        if (errorType === 'insufficient_credits') {
          triggerCreditFailure();
        } else if (errorType === 'plan_entitlement' || errorType === 'model_entitlement') {
          triggerPlanEntitlement();
        }
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Handle when user performs an improvement action
  const handleUserAction = (actionType) => {
    setUserHasActed(true);
    setShowSmartNudge(false);
    if (smartNudgeTimerRef.current) {
      clearTimeout(smartNudgeTimerRef.current);
    }
    
    // Record engagement loop step and track for upgrade trigger
    if (actionType === 'improve') {
      setHasImproved(true);
      recordStep(LOOP_STEPS.IMPROVE);
      trackAction('makeItBetter');
      trackWorkflowAction('makeItBetter');
    } else if (actionType === 'enhance') {
      setHasEnhanced(true);
      recordStep(LOOP_STEPS.ENHANCE);
    } else if (actionType === 'upscale') {
      setHasUpscaled(true);
      recordStep(LOOP_STEPS.UPSCALE);
      trackAction('upscale');
      trackWorkflowAction('upscale');
    }
    
    // Check if we should show low credits nudge after action
    checkLowCredits();
  };
  
  // Cleanup smart nudge timer on unmount
  useEffect(() => {
    return () => {
      if (smartNudgeTimerRef.current) {
        clearTimeout(smartNudgeTimerRef.current);
      }
    };
  }, []);

  // Generate variations of the current image
  const handleGenerateVariations = async (isManualRetry = false) => {
    if (!prompt.trim()) {
      toast.error('Please enter a prompt first');
      return;
    }
    
    const variationCost = 6; // 2 variations × 3 credits each
    const isAdmin = isAdminTester(user);
    logAdminBypass('ImageGenerator', 'handleGenerateVariations', user, isAdmin);
    
    if (!isAdmin && (user?.credits || 0) < variationCost) {
      toast.error(`Insufficient credits. Variations require ${variationCost} credits.`);
      return;
    }
    
    // Reset retry state for fresh attempts
    if (!isManualRetry) {
      variationsRetryCountRef.current = 0;
      setVariationsError(null);
    }
    
    setGeneratingVariations(true);
    setVariationsRetrying(isManualRetry);
    
    console.log('[ImageGenerator:handleGenerateVariations] Starting', {
      email: user?.email,
      isAdmin,
      prompt: prompt.substring(0, 50) + '...',
      style,
      isRetry: isManualRetry
    });
    
    const attemptVariations = async () => {
      const response = await api.post('/ai/generate-variations', {
        prompt: prompt.trim(),
        style,
        num_variations: 2,
        auto_enhance: autoEnhance
      });
      return response;
    };
    
    try {
      let response;
      let lastAttemptError = null;
      
      // Try with automatic retry on transient failures
      for (let attempt = 0; attempt <= MAX_AUTO_RETRIES; attempt++) {
        try {
          response = await attemptVariations();
          break; // Success
        } catch (error) {
          lastAttemptError = error;
          const shouldAutoRetry = attempt < MAX_AUTO_RETRIES && isRetriableError(error);
          
          if (shouldAutoRetry) {
            variationsRetryCountRef.current = attempt + 1;
            setVariationsRetrying(true);
            console.log(`[ImageGenerator:Variations] Auto-retrying (attempt ${attempt + 1})`, {
              errorType: classifyError(error)
            });
            toast.info('Request interrupted, retrying automatically...');
            await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
          } else {
            throw error;
          }
        }
      }
      
      if (!response) throw lastAttemptError;
      
      console.log('[ImageGenerator:handleGenerateVariations] Success', {
        variationsCount: response.data.variations?.length || 0,
        creditsUsed: response.data.credits_used,
        attempts: variationsRetryCountRef.current + 1
      });
      
      if (response.data.variations && response.data.variations.length > 0) {
        setVariations(response.data.variations);
        setVariationsError(null);
        if (!isAdmin) {
          await refreshUser();
        }
        const retryMsg = variationsRetryCountRef.current > 0 ? ' (recovered)' : '';
        toast.success(`Generated ${response.data.generated_count} variations!${isAdmin ? ' (Admin mode)' : ''}${retryMsg}`);
      }
    } catch (error) {
      console.error('[ImageGenerator:handleGenerateVariations] Failed:', {
        status: error.response?.status,
        detail: error.response?.data?.detail,
        attempts: variationsRetryCountRef.current + 1
      });
      
      // Store error for retry option
      setVariationsError(error);
      
      // Standardized error classification
      toast.error(getToastMessage(error, 'Failed to generate variations. Please try again.'));
      
      // Trigger upgrade modal for credit/plan errors (ONLY for normal users)
      if (!isAdminTester(user) && requiresUpgrade(error)) {
        const errorType = classifyError(error);
        if (errorType === 'insufficient_credits') {
          triggerCreditFailure();
        } else if (errorType === 'plan_entitlement' || errorType === 'model_entitlement') {
          triggerPlanEntitlement();
        }
      }
    } finally {
      setGeneratingVariations(false);
      setVariationsRetrying(false);
    }
  };
  
  // Select a variation as the main image
  const selectVariation = (variation) => {
    setResult({ image_base64: variation.image_base64, credits_used: 0 });
    setCurrentImageUrl(`data:image/png;base64,${variation.image_base64}`);
    setVariations([]);
    toast.success('Variation selected!');
  };

  const handleDownload = async () => {
    if (!result) return;
    
    try {
      const imageData = result.image_base64 || result.image_url;
      if (result.image_base64) {
        const link = document.createElement('a');
        link.href = `data:image/png;base64,${result.image_base64}`;
        link.download = `mockuppro-${Date.now()}.png`;
        link.click();
      } else if (result.image_url) {
        window.open(result.image_url, '_blank');
      }
      toast.success('Download started');
    } catch (error) {
      toast.error('Failed to download');
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Image Generator
        </h1>
        <p className="text-muted-foreground">
          Create AI images from your prompts ({isFirstFree ? 'First one FREE!' : '5 credits'})
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        {/* Input Section */}
        <Card>
          <CardContent className="p-6 space-y-6">
            <div className="space-y-4">
              <h2 className="text-lg font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Generate Image
              </h2>
              
              {/* First Free Banner */}
              <AnimatePresence>
                {isFirstFree && !checkingFirstFree && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="flex items-center gap-3 p-4 rounded-xl bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/20"
                    data-testid="first-free-banner"
                  >
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                      <Gift className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="font-medium text-green-700 dark:text-green-400">
                        Your first image is FREE!
                      </p>
                      <p className="text-sm text-green-600/80 dark:text-green-500/80">
                        Try it out - no credits needed for your first creation
                      </p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <div className="space-y-2">
              <Label htmlFor="prompt">Prompt</Label>
              <Textarea
                id="prompt"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="Describe the image you want to create..."
                className="min-h-[120px] resize-none"
                data-testid="prompt-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Style</Label>
              <Select value={style} onValueChange={setStyle}>
                <SelectTrigger data-testid="style-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {styles.map((s) => (
                    <SelectItem key={s.value} value={s.value}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Size</Label>
              <Select value={size} onValueChange={setSize}>
                <SelectTrigger data-testid="size-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {sizes.map((s) => (
                    <SelectItem key={s.value} value={s.value}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* AI Model Selector - Consistent with ImageToPrompt and Workflow */}
            <div className="bg-muted/30 rounded-xl p-4">
              <AIModelSelector
                selectedModel={selectedModel}
                onModelChange={setSelectedModel}
                variant="pills"
                compact={true}
              />
            </div>

            {/* Auto Enhance Toggle */}
            <div className="flex items-center justify-between bg-muted/30 rounded-xl p-4">
              <div className="flex items-center gap-2">
                <Wand2 className="w-4 h-4 text-primary" />
                <div>
                  <Label htmlFor="auto-enhance" className="cursor-pointer">Auto Enhance</Label>
                  <p className="text-xs text-muted-foreground">Automatically improve quality after generation</p>
                </div>
              </div>
              <Switch
                id="auto-enhance"
                checked={autoEnhance}
                onCheckedChange={setAutoEnhance}
                data-testid="auto-enhance-toggle"
              />
            </div>

            {/* Credit Cost Display */}
            {!isFirstFree && (
              <div className="flex items-center justify-between text-sm px-1">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Coins className="w-4 h-4" />
                  <span>Cost: <strong className="text-foreground">5 credits</strong></span>
                </div>
                <div className="text-muted-foreground">
                  Balance: <strong className={user?.credits < 5 ? 'text-red-500' : 'text-foreground'}>{user?.credits || 0}</strong>
                </div>
              </div>
            )}

            {/* Low Credit Warning */}
            <LowCreditWarning variant="inline" showWhen="low" />

            <Button
              onClick={handleGenerate}
              disabled={loading || !prompt.trim() || (!isFirstFree && !isAdminTester(user) && (user?.credits || 0) < 5)}
              className="w-full"
              data-testid="generate-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Generating{autoEnhance ? ' & Enhancing' : ''}...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  {isFirstFree ? 'Generate FREE Image' : 'Generate Image'}
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Result Section */}
        <Card>
          <CardContent className="p-6">
            {!result ? (
              <div className="aspect-square flex flex-col items-center justify-center bg-muted/30 rounded-xl">
                <ImageIcon className="w-16 h-16 text-muted-foreground/30 mb-4" />
                <p className="text-muted-foreground text-center">
                  Your generated image will appear here
                </p>
                {isFirstFree && (
                  <p className="text-sm text-green-600 mt-2 flex items-center gap-1">
                    <Gift className="w-4 h-4" />
                    First one's free!
                  </p>
                )}
              </div>
            ) : (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="space-y-4"
              >
                <div className="relative rounded-xl overflow-hidden bg-muted/30">
                  <img 
                    src={currentImageUrl}
                    alt="Generated"
                    className="w-full h-auto"
                    data-testid="generated-image"
                  />
                  {result.is_first_generation && (
                    <div className="absolute top-3 left-3 px-3 py-1.5 bg-green-500 text-white text-xs font-medium rounded-full shadow-lg flex items-center gap-1">
                      <Gift className="w-3 h-3" />
                      First Free Creation!
                    </div>
                  )}
                  {result.auto_enhanced && (
                    <div className="absolute top-3 right-3 px-3 py-1.5 bg-primary text-primary-foreground text-xs font-medium rounded-full shadow-lg flex items-center gap-1">
                      <Wand2 className="w-3 h-3" />
                      Enhanced
                    </div>
                  )}
                </div>
                
                {/* Enhance & Upscale Actions */}
                <ImageActions
                  imageUrl={currentImageUrl}
                  onImageUpdate={(newUrl) => {
                    setCurrentImageUrl(newUrl);
                    // Any image update means user is engaged
                    setUserHasActed(true);
                    setShowSmartNudge(false);
                  }}
                  originalPrompt={result?.input_data?.prompt || prompt}
                  imageStyle={result?.input_data?.style || style}
                  showDownload={false}
                  onMakeItBetter={() => handleUserAction('improve')}
                  onEnhance={() => handleUserAction('enhance')}
                  onUpscale={() => handleUserAction('upscale')}
                />
                
                {/* Conversion-Optimized Actions - encourages loop completion */}
                <ConversionOptimizedActions
                  onMakeItBetter={() => {
                    handleUserAction('improve');
                    // Trigger make it better through ImageActions
                    document.querySelector('[data-testid="make-it-better-btn"]')?.click();
                  }}
                  onEnhance={() => {
                    handleUserAction('enhance');
                    document.querySelector('[data-testid="enhance-btn"]')?.click();
                  }}
                  onUpscale={(scale) => {
                    handleUserAction('upscale');
                    document.querySelector('[data-testid="upscale-btn"]')?.click();
                  }}
                  onDownload={handleDownload}
                  onCreateMockup={() => window.location.href = '/dashboard/workflow'}
                  actionsCompleted={[hasImproved, hasEnhanced, hasUpscaled].filter(Boolean).length}
                  isImproved={hasImproved}
                  isEnhanced={hasEnhanced}
                  isUpscaled={hasUpscaled}
                  isProcessing={loading}
                  className="mt-4"
                />
                
                {/* Progressive Credit Warning - shows based on credit level */}
                <ProgressiveCreditWarning 
                  onUpgrade={() => window.location.href = '/pricing'}
                />
                
                {/* Generate Variations Button */}
                <Button
                  variant="outline"
                  onClick={() => handleGenerateVariations(false)}
                  disabled={generatingVariations || !prompt.trim()}
                  className="w-full"
                  data-testid="generate-variations-btn"
                >
                  {generatingVariations ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {variationsRetrying ? 'Retrying...' : 'Generating Variations...'}
                    </>
                  ) : variationsError ? (
                    <>
                      <RotateCcw className="w-4 h-4 mr-2" />
                      Retry Variations (6 credits)
                    </>
                  ) : (
                    <>
                      <Layers className="w-4 h-4 mr-2" />
                      Generate Variations (6 credits)
                    </>
                  )}
                </Button>
                
                {/* Display Variations */}
                {variations.length > 0 && (
                  <div className="space-y-2">
                    <Label>Variations (click to select)</Label>
                    <div className="grid grid-cols-2 gap-2">
                      {variations.map((variation) => (
                        <motion.div
                          key={variation.index}
                          initial={{ opacity: 0, scale: 0.95 }}
                          animate={{ opacity: 1, scale: 1 }}
                          className="relative rounded-lg overflow-hidden cursor-pointer border-2 border-transparent hover:border-primary transition-colors"
                          onClick={() => selectVariation(variation)}
                        >
                          <img
                            src={`data:image/png;base64,${variation.image_base64}`}
                            alt={`Variation ${variation.index}`}
                            className="w-full h-auto"
                          />
                          <div className="absolute bottom-1 right-1 px-2 py-0.5 bg-black/50 text-white text-xs rounded">
                            #{variation.index}
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                )}
                
                <div className="flex gap-4">
                  <Button 
                    onClick={handleDownload}
                    className="flex-1"
                    data-testid="download-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => setShowShareModal(true)}
                    className="flex-1"
                    data-testid="share-image-btn"
                  >
                    <Share2 className="w-4 h-4 mr-2" />
                    Share
                  </Button>
                </div>
                <Button 
                  variant="ghost"
                  onClick={() => {
                    setResult(null);
                    setCurrentImageUrl(null);
                    setVariations([]);
                    resetLoop(); // Reset engagement loop
                    setShowSmartNudge(false);
                    setUserHasActed(false);
                  }}
                  className="w-full"
                  data-testid="generate-another-btn"
                >
                  Generate Another
                </Button>
              </motion.div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Share Modal */}
      {result && (
        <ShareModal
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
          creationId={result.creation_id}
          imageUrl={currentImageUrl}
          title="My AI Generated Image"
        />
      )}
      
      {/* Smart Nudge - appears after generation if user doesn't act */}
      <SmartNudge
        show={showSmartNudge && result && !loading}
        delaySeconds={2.5}
        onMakeItBetter={() => {
          handleUserAction('improve');
          // Trigger make it better via ImageActions ref would be ideal,
          // but for now we just track the intent
        }}
        onEnhance={() => {
          handleUserAction('enhance');
        }}
        onUpscale={(scale) => {
          handleUserAction('upscale');
        }}
        onDismiss={() => setShowSmartNudge(false)}
      />
      
      {/* Upgrade Modal - triggered at key moments */}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={dismissModal}
        reason={triggerReason}
      />
    </div>
  );
};

export default ImageGenerator;
